export type Appearance =
  | 'dark'
  | 'light'
  | 'masters'
  | 'midnight'
  | 'graphite'
  | 'sage'
  | 'sand'
  | 'ocean'
  | 'clay'
  | 'plum'

export type AppearanceOption = {
  value: Appearance
  label: string
  preview: {
    canvas: string
    sidebar: string
    surface: string
    accent: string
  }
}

export const appearanceOptions: AppearanceOption[] = [
  { value: 'dark', label: 'Motion Dark', preview: { canvas: '#050505', sidebar: '#0e0e0e', surface: '#262626', accent: '#ededed' } },
  { value: 'light', label: 'Motion Light', preview: { canvas: '#ffffff', sidebar: '#f3f3f3', surface: '#f3f3f3', accent: '#171717' } },
  { value: 'masters', label: 'Masters of Scale', preview: { canvas: '#06072b', sidebar: '#0a0b31', surface: '#282245', accent: '#a995dc' } },
  { value: 'midnight', label: 'Midnight Blue', preview: { canvas: '#091126', sidebar: '#0d1831', surface: '#202f4d', accent: '#83a7ff' } },
  { value: 'graphite', label: 'Graphite', preview: { canvas: '#121417', sidebar: '#171b20', surface: '#2b323a', accent: '#b7c2d1' } },
  { value: 'sage', label: 'Sage', preview: { canvas: '#0e1d19', sidebar: '#142a24', surface: '#26483c', accent: '#a8d0b6' } },
  { value: 'sand', label: 'Sand', preview: { canvas: '#f7f1e7', sidebar: '#ece4d7', surface: '#ebe2d4', accent: '#745f49' } },
  { value: 'ocean', label: 'Ocean', preview: { canvas: '#061a24', sidebar: '#092530', surface: '#183d48', accent: '#72c2ce' } },
  { value: 'clay', label: 'Clay', preview: { canvas: '#fbf3ed', sidebar: '#f1e3d8', surface: '#f2e4d9', accent: '#a95540' } },
  { value: 'plum', label: 'Plum', preview: { canvas: '#180f21', sidebar: '#21142c', surface: '#3c2a4c', accent: '#c2a1d8' } },
]

const appearanceValues = new Set<Appearance>(appearanceOptions.map(option => option.value))
const lightAppearances = new Set<Appearance>(['light', 'sand', 'clay'])

export function isAppearance(value: unknown): value is Appearance {
  return typeof value === 'string' && appearanceValues.has(value as Appearance)
}

export function isLightAppearance(appearance: Appearance | string): boolean {
  return isAppearance(appearance) && lightAppearances.has(appearance)
}

export function getAppearance(): Appearance {
  try {
    const saved = window.localStorage.getItem('motion-appearance') || window.localStorage.getItem('motion-theme')
    if (isAppearance(saved)) return saved
    if (saved === 'system') return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch { /* Use the default when storage is unavailable. */ }
  return 'dark'
}

export function setAppearance(appearance: Appearance | string) {
  const next = isAppearance(appearance) ? appearance : 'dark'
  document.documentElement.dataset.theme = next
  document.documentElement.dataset.colorScheme = isLightAppearance(next) ? 'light' : 'dark'
  try { window.localStorage.setItem('motion-appearance', next) } catch { /* Keep the visible selection for this session. */ }
  window.dispatchEvent(new Event('motion-appearance-change'))
}
